class PromiseHelper {

    /**
     * Ensure the passed promise either resolves really quickly (within around 2 frames) or after a more significant amount of time
     * This can be useful if you may have a cached response and can display the data immediately without showing a loader but still don't just flash the loader for a few frames.
     *
     * @param {Promise<*>} promise The promise to slow down
     * @param {number} delay The number of milliseconds to wait for
     * @param {number} short_cutoff If the promise resolves within this amount of time then we return the result immediately. This value is in milliseconds
     * @return {Promise<*>}
     */
    static async short_delayed_promise(promise, delay, short_cutoff = 20 /* ~ 1 frame */) {
        let start = (new Date).getTime();
        let timer = PromiseHelper.timed_promise(delay);

        let result = await promise;
        let now = (new Date).getTime();

        // If the result was really quick then we can resolve immediately
        if (now - start < short_cutoff) {
            return result;
        }

        // Wait for the timer to complete before resolving
        await timer;
        return result;
    }

    /**
     * Ensure the passed promise cannot be resolved before the specified time limit. Useful for slowing down fast ajax requests
     *
     * @param {Promise} promise The promise to slow down
     * @param {number} limit The number of milliseconds to wait for
     * @return {Promise<>}
     */
    static delayed_promise(promise, limit) {
        return Promise.all([promise, PromiseHelper.timed_promise(limit)]).then(([data]) => data);
    }

    /**
     * Get a promise that will never be resolved
     *
     * @return {Promise<>}
     */
    static non_resolving_promise() {
        return new Promise(() => {});
    }

    /**
     * Get a promise that will be resolved after the specified timeout
     * @param {number} timeout The number of milliseconds to wait before resolving this promise
     * @param data The data to be used when resolving this promise
     * @return {Promise<>}
     */
    static timed_promise(timeout, data = null) {
        if (timeout === 0) {
            return Promise.resolve(data);
        }

        return new Promise((resolve) => setTimeout(() => resolve(data), timeout));
    }

}

export default PromiseHelper;
